import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMe } from "@/api/hooks";
import { Copy, Check } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import ConnectedAccounts from "@/components/profile/ConnectedAccounts";

export default function Profile() {
  const { data: meData, isLoading: loading } = useMe();
  const [copiedToken, setCopiedToken] = useState(null);

  const handleCopyToken = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(text);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a0a" }}>
        <div className="w-10 h-10 border-4 border-transparent rounded-full animate-spin" style={{ borderTopColor: "#c8f135" }} />
      </div>
    );
  }

  const user = meData || {};
  const referralUrl = user.referral_code
    ? `${window.location.origin}/GiveawayCopy?ref=${user.referral_code}`
    : null;
  const isCopied = copiedToken === referralUrl;

  return (
    <div className="min-h-screen" style={{ background: "#0a0a0a", fontFamily: "'Heebo', sans-serif" }}>
      <AppHeader />

      <div className="max-w-2xl mx-auto px-4 pt-[70px] pb-12" dir="rtl">
        {/* Profile header */}
        <div className="rounded-2xl p-6 mb-8" style={{ background: "#111", border: "1px solid #222" }}>
          <div className="flex items-start gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #c8f135, #a3e635)" }}
            >
              👤
            </div>
            <div className="flex-1">
              <h1 className="font-black text-white text-2xl mb-1">{user.display_name || user.full_name || user.email?.split("@")[0]}</h1>
              <p className="text-sm mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>
                {user.email}
              </p>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <p className="font-black text-xl" style={{ color: "#c8f135" }}>{user.total_points ?? 0}</p>
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>נקודות</p>
                </div>
                {user.rank && (
                  <div>
                    <p className="font-black text-xl text-white">#{user.rank}</p>
                    <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>דירוג</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick links */}
        <div className="space-y-3 mb-8">
          <Link
            to="/dashboard"
            className="rounded-2xl p-4 block transition-all hover:bg-[#1a1a1a]"
            style={{ background: "#111", border: "1px solid #222" }}
          >
            <div className="flex items-center justify-between">
              <p className="font-black text-white">לדשבורד</p>
              <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: "rgba(200,241,53,0.15)", color: "#c8f135" }}>
                בחר →
              </span>
            </div>
          </Link>
          <Link
            to="/leaderboard"
            className="rounded-2xl p-4 block transition-all hover:bg-[#1a1a1a]"
            style={{ background: "#111", border: "1px solid #222" }}
          >
            <div className="flex items-center justify-between">
              <p className="font-black text-white">🏆 לידרבורד</p>
              <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: "rgba(200,241,53,0.15)", color: "#c8f135" }}>
                בחר →
              </span>
            </div>
          </Link>
        </div>

        {/* Referral section */}
        {referralUrl && (
          <div className="mt-10 rounded-2xl p-6" style={{ background: "rgba(200,241,53,0.08)", border: "1px solid rgba(200,241,53,0.2)" }}>
            <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: "#c8f135" }}>
              🔗 הזמן חברים
            </p>
            <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.7)" }}>
              הזמן חברים וקבל 10 נקודות לכל הרשמה
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={referralUrl}
                readOnly
                className="flex-1 rounded-lg px-3 py-2 text-xs outline-none"
                style={{ background: "#0a0a0a", border: "1px solid #222", color: "#fff" }}
              />
              <button
                onClick={() => handleCopyToken(referralUrl)}
                className="px-3 py-2 rounded-lg font-bold text-xs transition-all flex items-center gap-1"
                style={{
                  background: isCopied ? "rgba(34,197,94,0.2)" : "#c8f135",
                  color: isCopied ? "#22c55e" : "#000",
                }}
              >
                {isCopied ? (
                  <>
                    <Check className="w-3 h-3" /> הוקל
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" /> העתק
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Campaign history */}
        {user.campaigns && user.campaigns.length > 0 && (
          <div className="mt-8">
            <p className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: "#c8f135" }}>
              היסטוריית קמפיינים
            </p>
            <div className="space-y-3">
              {user.campaigns.map((c) => (
                <div
                  key={c.id}
                  className="rounded-2xl p-4 flex items-center justify-between"
                  style={{ background: "#111", border: "1px solid #222" }}
                >
                  <div>
                    <p className="font-bold text-white text-sm">{c.name}</p>
                    <p className="text-xs" style={{ color: "#666" }}>
                      {c.completions} משימות · {c.status === "active" ? "פעיל" : c.status === "archived" ? "הסתיים" : "טיוטה"}
                    </p>
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-lg" style={{ color: "#c8f135" }}>{c.campaign_points}</p>
                    <p className="text-xs" style={{ color: "#666" }}>נקודות</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <ConnectedAccounts />
      </div>
    </div>
  );
}
